/**
 * Header del ticket + barra azioni V3 (prototipo `Centro Ticket.dc.html`
 * L173-L224).
 *
 * Contiene: numero, stato, priorita, SLA e canale in badge; oggetto; riga
 * meta con gruppo, richiedente, assegnatari e data di apertura; la barra
 * azioni con gerarchia primario / secondari / overflow (`actionsModel.ts`);
 * la presence ("Anche qui: …") e l'indicatore "X sta scrivendo…"; il
 * "Torna alla coda" quando la coda non e affiancata (narrow, oppure area
 * rappresentante, dove il dettaglio e una pagina a se).
 *
 * Parita con `TicketDetail.tsx:418-456` (torna alla coda + header),
 * `:529-625` (azioni) e `:630-639` (presence/typing). Il componente e
 * presentazionale: ogni azione risale al chiamante via `onAction(id)`, che
 * apre la modale o esegue la chiamata REST. Con `busy` la barra resta intera
 * ma inerte, come i bottoni attuali con `disabled={busy}`.
 *
 * Veste v4 (T35): con `useSupportLook() === 'v4'` monta `v4/TicketHero` con
 * gli STESSI dati e lo stesso stato (oggetto in modifica, menu, presence,
 * resa dei bottoni); con 'v3' il DOM resta quello di sempre.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  AlignLeft,
  ArrowLeft,
  BookMarked,
  CheckCircle2,
  GitMerge,
  Grab,
  Info,
  MoreHorizontal,
  Pencil,
  Repeat,
  Reply,
  ShieldOff,
  Trash2,
  UserPlus,
  Users,
  XCircle,
} from 'lucide-react';
import { S3Avatar, S3Badge, S3Menu, S3MenuItem } from './primitives';
import { photoUrl } from '../../../utils/photoUrl';
import { originVisual, priorityVisual, statusVisual, type S3SlaBadge } from './visual';
import type { S3Action, S3ActionBar, S3ActionId } from './actionsModel';
import { formatDate, initialsOf } from '../constants';
import { displayStatus } from '../displayStatus';
import DerivedStatusMark from '../DerivedStatusMark';
import type { PresenceViewer } from '../../../hooks/useSupportSocket';
import type { ShareLinksPayload, ShareLinksResult, TicketDetail } from '../../../services/supportApi';
import { useSupportLook } from '../v4/lookContext';
import TicketHero from '../v4/TicketHero';
import ShareTicketButton, { type TicketPrintConfig } from '../ShareTicketButton';

/** Limiti dell'oggetto: gli stessi del backend (`setSubject`, schema `maxlength`). */
const SUBJECT_MIN = 3;
const SUBJECT_MAX = 200;

const ICONS: Record<S3ActionId, React.ReactNode> = {
  take: <Grab size={13} />,
  reply: <Reply size={13} />,
  resolve: <CheckCircle2 size={13} />,
  status: <AlignLeft size={13} />,
  transfer: <Repeat size={13} />,
  assignees: <UserPlus size={13} />,
  merge: <GitMerge size={13} />,
  // Stessa icona del bottone attuale (TicketDetail.tsx:620): e l'azione
  // distruttiva, deve restare riconoscibile.
  ignoreSender: <ShieldOff size={13} />,
  setRequester: <UserPlus size={13} />,
  close: <XCircle size={13} />,
  delete: <Trash2 size={13} />,
  knowledge: <BookMarked size={13} />,
  details: <Info size={13} />,
};

export interface TicketHeaderCardProps {
  ticket: TicketDetail;
  bar: S3ActionBar;
  /** Esito di `slaBadge(classifySla(ticket, new Date()))`; `null` senza SLA. */
  sla: S3SlaBadge | null;
  onAction: (id: S3ActionId) => void;
  narrow: boolean;
  onBack: () => void;
  /**
   * Mostra "Torna alla coda". Default `narrow`: nel Centro Ticket admin la
   * coda e affiancata sopra i 1024px e il ritorno non serve. L'area
   * rappresentante lo tiene SEMPRE acceso, perche li il dettaglio e una
   * pagina a se (parita con `TicketDetail.tsx:418-424`).
   */
  showBack?: boolean;
  /**
   * Etichetta del ritorno (v4, N21: «Torna allo smistamento» secondo la
   * provenienza). Default «Torna alla coda», l'unica del v3.
   */
  backLabel?: string;
  viewers: PresenceViewer[];
  typingName: string | null;
  /** Una chiamata al backend e in corso: la barra resta visibile ma inerte. */
  busy?: boolean;
  /** Il mio id operatore: serve solo a escludermi dalla presence. */
  selfId?: string;
  /**
   * Correzione dell'oggetto (Task 24/E): uno dei due permessi di modifica dei
   * messaggi. La matita compare solo con questo e ticket non chiuso.
   */
  canEditSubject?: boolean;
  /** Salvataggio dell'oggetto: deve RIGETTARE in caso di errore (resta in modifica). */
  onEditSubject?: (subject: string) => Promise<void>;
  /** Link firmati agli allegati per «Condividi via email» (adapter operatore). */
  createShareLinks?: (payload: ShareLinksPayload) => Promise<ShareLinksResult>;
  /** «Stampa» nel menu Condividi (adapter operatore). */
  print?: TicketPrintConfig;
}

/** Nominativo del richiedente: `requester.nome` o lo snapshot dell'associato. */
const requesterName = (ticket: TicketDetail): string =>
  ticket.requester?.nome ||
  ticket.associateSnapshot?.nominativo ||
  ticket.requester?.email ||
  '—';

const TicketHeaderCard: React.FC<TicketHeaderCardProps> = ({
  ticket,
  bar,
  sla,
  onAction,
  narrow,
  onBack,
  showBack,
  backLabel = 'Torna alla coda',
  viewers,
  typingName,
  busy,
  selfId,
  canEditSubject = false,
  onEditSubject,
  createShareLinks,
  print,
}) => {
  const look = useSupportLook();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLButtonElement>(null);
  /**
   * v4: chiudendo il menu (Esc, click fuori, scelta di una voce) il focus
   * torna sul bottone ⋯. `S3Menu` lo riporta solo se il focus e ancora nel
   * menu al momento della pulizia, ma a quel punto il nodo e gia smontato e il
   * focus e finito sul body. Scegliendo una voce che apre una modale, la
   * modale ricorda il ⋯ e ci torna alla chiusura. Il v3 resta com'e.
   */
  const closeMoreV4 = useCallback(() => {
    setMoreOpen(false);
    moreRef.current?.focus({ preventScroll: true });
  }, []);
  // Bozza dell'oggetto in modifica (null = non in modifica).
  const [subjectDraft, setSubjectDraft] = useState<string | null>(null);
  const [savingSubject, setSavingSubject] = useState(false);
  const subjectEditable = canEditSubject && Boolean(onEditSubject) && ticket.status !== 'chiuso';
  const subjectValid = (subjectDraft || '').trim().length >= SUBJECT_MIN && (subjectDraft || '').trim().length <= SUBJECT_MAX;
  const saveSubject = async () => {
    if (subjectDraft === null || !onEditSubject || savingSubject || !subjectValid) return;
    const next = subjectDraft.trim();
    if (next === ticket.subject) {
      setSubjectDraft(null);
      return;
    }
    setSavingSubject(true);
    try {
      await onEditSubject(next);
      setSubjectDraft(null);
    } catch {
      // L'errore lo mostra il chiamante (toast): la bozza resta in modifica.
    } finally {
      setSavingSubject(false);
    }
  };
  const st = statusVisual(ticket.status);
  const shown = displayStatus(ticket);
  const pr = priorityVisual(ticket.priority);
  const or = originVisual(ticket.origin);
  const assegnati = (ticket.assignees || []).map((a) => a.nome).filter(Boolean);
  // Escludo me stesso dalla presence (come TicketDetail.tsx:396). La foto si
  // risolve UNA volta per viewer: chi ce l'ha e rappresentato dall'avatar, chi
  // non ce l'ha dal nome nel testo — mai tutti e due per la stessa persona.
  const otherViewers = useMemo(
    () => (viewers || [])
      .filter((v) => !selfId || v.repId !== selfId)
      .map((v) => ({ ...v, photo: photoUrl(v.foto) })),
    [viewers, selfId]
  );
  const viewersConFoto = otherViewers.filter((v) => v.photo);
  const viewersSenzaFoto = otherViewers.filter((v) => !v.photo);

  /**
   * Un'azione inerte non usa `disabled`: sparirebbe dall'ordine di
   * tabulazione e lo screen reader non leggerebbe piu il motivo. Resta un
   * bottone focusabile con `aria-disabled`, click a vuoto e motivo esposto
   * sia come `title` sia come testo riservato agli screen reader.
   */
  const pill = (a: S3Action, primary?: boolean, extraClass?: string) => {
    const off = Boolean(a.disabled || busy);
    const reasonId = off && a.title ? `s3-act-why-${a.id}` : undefined;
    return (
      <React.Fragment key={a.id}>
        <button
          type="button"
          className={`s3-pill${primary ? ' s3-pill--primary' : ''}${extraClass ? ` ${extraClass}` : ''}`}
          aria-disabled={off || undefined}
          aria-describedby={reasonId}
          title={a.title}
          onClick={() => {
            if (off) return;
            onAction(a.id);
          }}
        >
          {ICONS[a.id]} {a.label}
        </button>
        {reasonId && (
          <span id={reasonId} className="s3-sr-only">
            {a.title}
          </span>
        )}
      </React.Fragment>
    );
  };

  if (look === 'v4') {
    return (
      <TicketHero
        ticket={ticket}
        bar={bar}
        sla={sla}
        onAction={onAction}
        busy={busy}
        showBack={showBack ?? narrow}
        onBack={onBack}
        backLabel={backLabel}
        requesterName={requesterName(ticket)}
        createShareLinks={createShareLinks}
        print={print}
        assegnati={assegnati}
        viewersConFoto={viewersConFoto}
        viewersSenzaFoto={viewersSenzaFoto}
        typingName={typingName}
        subject={{
          editable: subjectEditable,
          draft: subjectDraft,
          setDraft: setSubjectDraft,
          save: () => void saveSubject(),
          saving: savingSubject,
          valid: subjectValid,
          maxLength: SUBJECT_MAX,
        }}
        menu={{ open: moreOpen, setOpen: setMoreOpen, close: closeMoreV4, triggerRef: moreRef }}
        icons={ICONS}
        renderAction={(a, primary) => pill(a, primary, 's4-hero__btn')}
      />
    );
  }

  return (
    <div className="s3-card s3-thead">
      {(showBack ?? narrow) && (
        <button type="button" className="s3-link s3-thead__back" onClick={onBack}>
          <ArrowLeft size={13} /> {backLabel}
        </button>
      )}

      <div className="s3-thead__top">
        <div className="s3-thead__main">
          <div className="s3-thead__badges">
            <span className="s3-thead__num">#{ticket.ticketNumber}</span>
            {shown.derived ? <DerivedStatusMark d={shown} /> : <S3Badge tone={st.tone}>{st.label}</S3Badge>}
            <S3Badge tone={pr.tone}>PRIORITÀ {pr.label}</S3Badge>
            {or.label && <S3Badge tone={or.tone}>{or.label}</S3Badge>}
            {sla && (
              <S3Badge tone={sla.tone} title={sla.title}>
                {sla.label}
              </S3Badge>
            )}
          </div>

          {subjectDraft !== null ? (
            <div className="s3-thead__subject-edit">
              <input
                type="text"
                className="s3-search s3-search--plain"
                aria-label="Oggetto del ticket"
                value={subjectDraft}
                maxLength={SUBJECT_MAX}
                disabled={savingSubject}
                autoFocus
                onChange={(e) => setSubjectDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void saveSubject();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    setSubjectDraft(null);
                  }
                }}
              />
              <button
                type="button"
                className="s3-pill s3-pill--sm"
                disabled={savingSubject}
                onClick={() => setSubjectDraft(null)}
              >
                Annulla
              </button>
              <button
                type="button"
                className="s3-pill s3-pill--sm s3-pill--primary"
                disabled={savingSubject || !subjectValid}
                onClick={() => void saveSubject()}
              >
                Salva
              </button>
            </div>
          ) : (
            <div className="s3-thead__subject">
              <h2 className="s3-h2">{ticket.subject}</h2>
              {subjectEditable && (
                <button
                  type="button"
                  className="s3-pill s3-pill--sm s3-pill--icon"
                  aria-label="Modifica oggetto"
                  title="Modifica oggetto"
                  disabled={busy}
                  onClick={() => setSubjectDraft(ticket.subject || '')}
                >
                  <Pencil size={11} aria-hidden="true" />
                </button>
              )}
            </div>
          )}

          <div className="s3-thead__meta">
            <span className="s3-thead__group">
              <Users size={12} aria-hidden="true" /> {ticket.groupId?.nome || '—'}
            </span>
            <span className="s3-thead__sep">·</span>
            <span>
              Richiedente <strong>{requesterName(ticket)}</strong>
            </span>
            <span className="s3-thead__sep">·</span>
            <span>
              Assegnato a <strong>{assegnati.length ? assegnati.join(', ') : 'nessuno'}</strong>
            </span>
            <span className="s3-thead__sep">·</span>
            <span>Aperto il {formatDate(ticket.createdAt)}</span>
          </div>
        </div>

        <div className="s3-thead__actions">
          {bar.primary && pill(bar.primary, true)}
          {bar.secondary.map((a) => pill(a))}
          <ShareTicketButton ticket={ticket} busy={busy} createShareLinks={createShareLinks} print={print} />
          {bar.overflow.length > 0 && (
            <>
              <button
                ref={moreRef}
                type="button"
                className={`s3-pill s3-pill--icon${moreOpen ? ' s3-pill--active' : ''}`}
                title="Altre azioni"
                aria-label="Altre azioni"
                aria-haspopup="menu"
                aria-expanded={moreOpen}
                aria-disabled={busy || undefined}
                onClick={() => {
                  if (busy) return;
                  setMoreOpen((v) => !v);
                }}
              >
                <MoreHorizontal size={15} />
              </button>
              <S3Menu
                open={moreOpen}
                onClose={() => setMoreOpen(false)}
                ariaLabel="Altre azioni"
                triggerRef={moreRef}
              >
                {bar.overflow.map((a) => (
                  <S3MenuItem
                    key={a.id}
                    danger={a.danger}
                    title={a.title}
                    aria-disabled={a.disabled || busy || undefined}
                    onClick={() => {
                      if (a.disabled || busy) return;
                      setMoreOpen(false);
                      onAction(a.id);
                    }}
                  >
                    {ICONS[a.id]} {a.label}
                  </S3MenuItem>
                ))}
              </S3Menu>
            </>
          )}
        </div>
      </div>

      {(otherViewers.length > 0 || typingName) && (
        <div className="s3-presence">
          {otherViewers.length > 0 && (
            <S3Badge tone="warn" small>
              {/* Chi ha la foto e nell'avatar (col nome nel `title`), chi non ce
                  l'ha resta nell'elenco dei nomi: nessun nome ripetuto due volte. */}
              Anche qui:{' '}
              {viewersConFoto.map((v) => (
                <S3Avatar key={v.repId} initials={initialsOf(v.name)} size={16} src={v.photo} title={v.name} />
              ))}
              {viewersSenzaFoto.map((v) => v.name).join(', ')}
            </S3Badge>
          )}
          {typingName && (
            <span className="s3-typing">
              <Pencil size={12} aria-hidden="true" /> {typingName} sta scrivendo…
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default TicketHeaderCard;
